/**
 * App component module.
 * @module App
 *
 */
import React, {Suspense} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router-dom';
import history from './history';
import cache from '../src/shared/utils/swrCache';
import ModalManager from './shared/components/modals/ModalManager';
import {_getBrand, _setEntryPath} from './shared/store';
import {ThemeProvider} from 'styled-components/macro';
import './App.css';
import debug from 'debug';
import {PORTAL_NAME} from './config.json';
import {SWRConfig} from 'swr';
import axiosInstance from './shared/utils/axios';
import {getPortalTheme} from './shared/utils/utils';
import {getBlackWhiteWithOpacity} from './shared/utils/colors';
import {ErrorBoundary} from 'react-error-boundary';
import {AppErrorTile, logError} from './shared/components/ErrorBounds';
import qs from 'query-string';

const AdvisorUI = React.lazy(() => import('./advisor/routes'));
const OperationsUI = React.lazy(() => import('./ops/routes'));
const InvestorUI = React.lazy(() => import('./investor/routes'));

const log = debug('circleblack:app');

const theme = getPortalTheme();

/**
 * App component
 * @name App
 */
const App = ({
  setEntryPath,
  getBrand,
  location,
  product,
}) => {
  const [awesomeTheme, setAT] = React.useState(theme);
  React.useEffect(() => {
    log('Mounting');
    localStorage.setItem('portal', PORTAL_NAME);
    const noRedirectRoutes = ['/login', '/logout', '/'];
    const redirectPath = !noRedirectRoutes.includes(location.pathname) ? location.pathname : '/';
    // !noRedirectRoutes.includes(location.pathname) ? location.pathname : (
    //   (isAuthorized(['FIRMMGR', 'BRANCHMGR', 'ADVISOR', 'CPLO']) && '/analyze/dashboard')
    //   || (isAuthorized(['PRODMGR', 'PRODSUPP']) && '/product/firms')
    //   || (isAuthorized(['ASSOC']) && '/assist')
    //   || '/management/welcome');
    if (noRedirectRoutes.includes(location.pathname)) {
      const locationSearch = qs.parse(window.location.search);
      const ssoToken = locationSearch.ssoToken || locationSearch.sso;
      log('noRedirectRoutes includes', location.pathname);
      if (ssoToken) {
        history.push({
          pathname: '/',
          search: location.search,
        });
      } else if (!localStorage.getItem('token')) {
        history.push({
          pathname: '/login',
          search: location.search,
        });
      } else {
        history.push('/');
      }
    }
    if (!location.pathname.match('^(?:\/user\/password\/|\/firstLogin\/)')) {
      log('Setting redirect', redirectPath + (location.search || ''));
      setEntryPath({
        pathname: redirectPath + (location.search || ''),
        redirect: true,
      });
    }
    log('Setting brand', getBrand());
  }, []);
  React.useEffect(() => {
    if (product && product.brand && product.brand.darkTheme) {
      setAT(product.brand.lightTheme);
    } else {
      setAT(theme);
    }
  }, [product]);
  const brand = product && product.brand;

  log('version', process.env.REACT_APP_VERSION);
  log('hash', process.env.REACT_APP_HASH);

  log('Rendering', product); // ETODO Replace Suspense with splash screen
  return <ThemeProvider theme={awesomeTheme}>
    <SWRConfig value={{
      provider: () => cache,
      fetcher: url => axiosInstance.get(url).then(res => res.data),
      revalidateOnFocus: false,
      dedupingInterval: 5000,
      focusThrottleInterval: 10000,
    }}>
      <style>{`
    html {background:${(brand && (brand.darkTheme || brand.lightTheme) || theme).backgroundColor};
    // overflow: hidden;height: 100%;
    }
    body {
      font-family: ${theme.bodyText.name};
      color: ${getBlackWhiteWithOpacity(0.9, theme)};
    }
    a {
      text-decoration: none;
      cursor: pointer;
      color: ${theme.primaryColor};
    }
    `}</style>
      <style>
        {theme.neumorphic ? `
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.09);
          -webkit-border-top-left-radius: 4px 4px;
          -webkit-border-top-right-radius: 4px 4px;
          -webkit-border-bottom-left-radius: 4px 4px;
          -webkit-border-bottom-right-radius: 4px 4px;
          box-shadow: inset -1px -1px 0 0 rgba(0, 0, 0, 0.2),
                      inset 1px 1px 0 0 rgba(255, 255, 255, 0.07);
        }
        ` : ''}
      </style>
      <ErrorBoundary
        FallbackComponent={AppErrorTile}
        onError={logError}>
        <Suspense fallback={<div/>}>
          {PORTAL_NAME === 'advisor' && <AdvisorUI/>}
          {PORTAL_NAME === 'ops' && <OperationsUI/>}
          {PORTAL_NAME === 'investor' && <InvestorUI/>}
        </Suspense>
        <ModalManager/>
      </ErrorBoundary>
    </SWRConfig>
  </ThemeProvider>;
};

const mapStateToProps = state => ({
  session: state.session,
  product: state.product,
});

const mapDispatchToProps = dispatch => ({
  setEntryPath: path => dispatch(_setEntryPath(path)),
  getBrand: () => dispatch(_getBrand()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));

App.propTypes = {
  /**
   * Function that sets the entry page on login
   */
  setEntryPath: PropTypes.func.isRequired,
  /**
   * Function that gathers the Brand
   */
  getBrand: PropTypes.func.isRequired,
  /**
   * User Session, includes token and email.
   */
  session: PropTypes.shape({
    token: PropTypes.string.isRequired,
    email: PropTypes.string,
  }).isRequired,
  /**
   * The Product
   */
  product: PropTypes.shape({
    brand: PropTypes.object.isRequired,
  }).isRequired,
  /**
   * WithRouter Prop
   */
  location: PropTypes.object.isRequired,
};
